import { Injectable } from '@angular/core';
import { OriginalFormValues } from './parking-request-form-state.service';

/**
 * Interface for current form values used in payload building
 */
export interface CurrentFormValues {
  bookingValue: string | null;
  startDateValue: Date | null;
  endDateValue: Date | null;
  parkingPlaceValue: string | null;
  disabledParkingPlaceValue: boolean;
  paidValue: boolean;
  licensePlateNotKnownValue: boolean;
  licensePlateValue: string;
  licensePlateCountryCodeValue: string | null;
}

/**
 * Service to build API payloads for parking request forms
 */
@Injectable()
export class ParkingRequestPayloadBuilderService {
  /**
   * Build full payload for create operations
   */
  buildFullPayload(values: CurrentFormValues): any {
    return {
      booking: values.bookingValue,
      start_date: values.startDateValue,
      end_date: values.endDateValue,
      parkingPlace: values.parkingPlaceValue,
      disabled_parking_place: values.disabledParkingPlaceValue,
      paid: values.paidValue,
      license_plate_not_known: values.licensePlateNotKnownValue,
      license_plate: values.licensePlateNotKnownValue ? null : values.licensePlateValue,
      license_plate_country_code: values.licensePlateNotKnownValue ? null : values.licensePlateCountryCodeValue
    };
  }

  /**
   * Build payload with only changed fields for efficient PATCH requests
   */
  buildChangedFieldsPayload(currentValues: CurrentFormValues, originalValues: OriginalFormValues, parkingRequestId: string | null): any {
    if (!originalValues) {
      // For create mode, return all fields
      return this.buildFullPayload(currentValues);
    }

    // Parse numeric ID string
    const idString = parkingRequestId;
    const parsedId = idString ? parseInt(idString, 10) : 0;

    if (isNaN(parsedId) || parsedId === 0) {
      throw new Error(`Invalid parking request ID: ${idString}`);
    }

    const changedFields: any = {
      id: parsedId
    };

    // Check each field for changes and include only changed ones
    const bookingChanged = currentValues.bookingValue !== originalValues.bookingValue;

    if (bookingChanged) {
      changedFields.booking = currentValues.bookingValue;
    }

    // Use simple date comparison
    const startDateChanged = (currentValues.startDateValue?.getTime() || null) !== (originalValues.startDateValue?.getTime() || null);
    if (startDateChanged) {
      changedFields.start_date = currentValues.startDateValue;
    }

    const endDateChanged = (currentValues.endDateValue?.getTime() || null) !== (originalValues.endDateValue?.getTime() || null);
    if (endDateChanged) {
      changedFields.end_date = currentValues.endDateValue;
    }

    if (currentValues.parkingPlaceValue !== originalValues.parkingPlaceValue) {
      changedFields.parkingPlace = currentValues.parkingPlaceValue;
    }

    if (currentValues.disabledParkingPlaceValue !== originalValues.disabledParkingPlaceValue) {
      changedFields.disabled_parking_place = currentValues.disabledParkingPlaceValue;
    }

    if (currentValues.paidValue !== originalValues.paidValue) {
      changedFields.paid = currentValues.paidValue;
    }

    if (currentValues.licensePlateNotKnownValue !== originalValues.licensePlateNotKnownValue) {
      changedFields.license_plate_not_known = currentValues.licensePlateNotKnownValue;
    }

    if (currentValues.licensePlateValue !== originalValues.licensePlateValue) {
      changedFields.license_plate = currentValues.licensePlateNotKnownValue ? null : currentValues.licensePlateValue;
    }

    if (currentValues.licensePlateCountryCodeValue !== originalValues.licensePlateCountryCodeValue) {
      changedFields.license_plate_country_code = currentValues.licensePlateNotKnownValue ? null : currentValues.licensePlateCountryCodeValue;
    }

    return changedFields;
  }
}
